import styled from '@emotion/styled'
import { OrderActions, OrderDetail, OrderEventModify } from 'api'
import { Locale, Translation } from 'locale'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { preparedStagingOrderIdsSelector } from 'selectors/selectionSelectors'
import { GridActions } from 'store/grid'
import { CustomToolbarComponentProps, GridBulkDateUpdateFields, GridId } from 'store/grid/gridTypes'
import { filterWeekend } from 'utils'
import { isNotEmpty, isNotNil } from 'utils/predicate'
import { LabelPosition, TextButton, TextButtonType } from 'view/components/buttons'
import { DatePickerField } from 'view/components/inputs'
import { TermSelectboxField } from 'view/shared/entity'
import { useModal } from 'view/shared/modal'
import { TradingUserOnly } from 'view/shell/TradingUserOnly'
import {
  bulkDateUpdateDialogArgs,
  createMapOrderDataToOrderEventModify,
  createPreparingOrdersFilter,
  createRepeatedTermsFilter
} from './bulkDateUpdateHelpers'
import { getDateEditorFormat } from 'model'
import { useUserPreference } from 'view/hooks'
import { useBulkDateUpdateValues } from 'view/hooks/grid'
import { minimumMarketTradeDateSelector } from 'selectors/platformSelectors'

export const CustomDatePickerField = styled(DatePickerField)`
  margin-right: 10px;
  .field-input {
    max-width: 110px;
  }
`

export const CustomTermSelectboxField = styled(TermSelectboxField)`
  margin-right: 10px;
  .field-input {
    max-width: 110px;
  }
`

export const BulkDateUpdateContainer = styled.div`
  display: flex;
  margin-right: 20px;
`

interface BulkDateUpdateInputComponentProps extends CustomToolbarComponentProps {
  gridId: GridId
}

export const BulkDateUpdateInput: React.FC<BulkDateUpdateInputComponentProps> = ({ gridId, gridApi }) => {
  const { showDialog } = useModal()
  const dispatch = useDispatch()
  const { startDate, endDate, term } = useBulkDateUpdateValues(gridId)
  const minMarketTradeDate = useSelector(minimumMarketTradeDateSelector)
  const preparedOrderIds = useSelector(preparedStagingOrderIdsSelector)
  const userPreference = useUserPreference()

  const handleChange = (bulkUpdateFields: GridBulkDateUpdateFields) => {
    dispatch(
      GridActions.saveGridBulkDateUpdateFields(gridId, 'default', {
        startDate: bulkUpdateFields.startDate,
        endDate: bulkUpdateFields.endDate,
        term: bulkUpdateFields.term
      })
    )
  }

  const handleUpdate = async () => {
    const orderDetails: OrderDetail[] = gridApi?.getSelectedRows() ?? []
    const childEvents: OrderEventModify[] = orderDetails
      .filter(createRepeatedTermsFilter(term))
      .filter(createPreparingOrdersFilter(preparedOrderIds))
      .map(createMapOrderDataToOrderEventModify(startDate, endDate, term))

    const willOverwriteStartDate = orderDetails.some(orderDetail => isNotNil(orderDetail.order.startDate)) && isNotNil(startDate)
    const willOverwriteEndDate = orderDetails.some(orderDetail => isNotNil(orderDetail.order.endDate)) && isNotNil(endDate)
    const willOverwriteTerm = orderDetails.some(orderDetail => isNotNil(orderDetail.order.term)) && isNotNil(term)
    const willOverwrite = isNotEmpty(childEvents) && (willOverwriteStartDate || willOverwriteEndDate || willOverwriteTerm)

    if (willOverwrite && (await showDialog(bulkDateUpdateDialogArgs)) !== Translation.TC_BUTTON_YES) {
      return
    }

    const hasUpdates = isNotNil(startDate) || isNotNil(endDate) || isNotNil(term)

    if (hasUpdates) {
      dispatch(OrderActions.bulkModify({ childEvents }))
    }
  }

  return (
    <TradingUserOnly>
      <BulkDateUpdateContainer>
        <CustomDatePickerField
          value={startDate}
          onUpdate={value => {
            handleChange({
              term: null,
              startDate: value,
              endDate: endDate
            })
          }}
          label={Locale.translate(Translation.TC_START_DATE)}
          labelPosition={LabelPosition.Left}
          minDate={minMarketTradeDate}
          filterDate={filterWeekend}
          dateFormat={getDateEditorFormat(userPreference.dateFormat)}
        />
        <CustomDatePickerField
          value={endDate}
          onUpdate={value => {
            handleChange({
              term: null,
              startDate: startDate,
              endDate: value
            })
          }}
          label={Locale.translate(Translation.TC_END_DATE)}
          labelPosition={LabelPosition.Left}
          minDate={new Date()}
          filterDate={filterWeekend}
          dateFormat={getDateEditorFormat(userPreference.dateFormat)}
        />
        <CustomTermSelectboxField
          value={term}
          onUpdate={value => {
            handleChange({
              term: value,
              startDate: null,
              endDate: null
            })
          }}
          label={Locale.translate(Translation.TC_BULK_DATE_UPDATE_TERM)}
          labelPosition={LabelPosition.Left}
        />
        <TextButton type={TextButtonType.Primary} onClick={handleUpdate}>
          {Locale.translate(Translation.TC_BUTTON_UPDATE)}
        </TextButton>
      </BulkDateUpdateContainer>
    </TradingUserOnly>
  )
}
